// =============================================================================
// BookingFlow.tsx — PAY-6 / CLINIO-202
// =============================================================================
// Extended public booking widget that supports prepayment via Clinio Pay.
//
// Steps for services WITHOUT prepayment (require_prepayment = false):
//   service → clinician → date → slot → patient → confirmation
//   (same as PublicBooking.tsx, calls book-as-patient directly)
//
// Steps for services WITH prepayment (require_prepayment = true):
//   service → clinician → date → slot → patient → payment → awaiting_payment
//   - patient step: creates a booking_intent row, calls request_booking_pay_link RPC
//   - redirects patient to Clinio Pay hosted checkout
//   - on return: polls booking_intent status; shows confirmation when webhook fires
//
// Patient-facing UX for non-prepayment bookings is UNCHANGED (PAY-6 constraint).
// DKK only — no currency picker (PAY-1 ADD §8).
// =============================================================================

import { useState, useCallback, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Loader2,
  ChevronRight,
  ChevronLeft,
  Check,
  User,
  Clock,
  Calendar,
  Stethoscope,
  CreditCard,
  AlertCircle,
} from 'lucide-react';
import { format, addDays, startOfDay, isBefore, parseISO } from 'date-fns';
import { da } from 'date-fns/locale';
import {
  useBookableServices,
  useAvailableSlots,
  useBookAppointment,
  type BookableService,
  type BookableClinician,
  type AvailableSlot,
} from '@/hooks/usePublicBooking';
import {
  useCreateBookingIntent,
  useRequestBookingPayLink,
  useBookingIntentStatus,
} from '@/hooks/useBookingFlow';

// ─── Step definitions ─────────────────────────────────────────────────────────

type Step =
  | 'service'
  | 'clinician'
  | 'date'
  | 'slot'
  | 'patient'
  | 'payment'         // prepayment step (redirect to Clinio Pay)
  | 'awaiting_payment' // post-redirect: polling for webhook confirmation
  | 'confirmation';

const STANDARD_STEPS: Step[] = ['service', 'clinician', 'date', 'slot', 'patient', 'confirmation'];
const PREPAY_STEPS:   Step[] = ['service', 'clinician', 'date', 'slot', 'patient', 'payment', 'awaiting_payment'];

// ─── Patient form schema ──────────────────────────────────────────────────────

const PatientSchema = z.object({
  first_name: z.string().min(1, 'Fornavn er påkrævet'),
  last_name:  z.string().min(1, 'Efternavn er påkrævet'),
  email:      z.string().email('Ugyldig e-mailadresse'),
  phone:      z.string().optional(),
  notes:      z.string().optional(),
});

type PatientFormValues = z.infer<typeof PatientSchema>;

// ─── Component ────────────────────────────────────────────────────────────────

export function BookingFlow() {
  const { clinicSlug } = useParams<{ clinicSlug: string }>();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();

  const [step, setStep] = useState<Step>('service');
  const [selectedService,   setSelectedService]   = useState<BookableService | null>(null);
  const [selectedClinician, setSelectedClinician] = useState<BookableClinician | null>(null);
  const [anyClinicianSelected, setAnyClinicianSelected] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedSlot, setSelectedSlot] = useState<AvailableSlot | null>(null);

  // Standard booking result
  const [confirmationCode, setConfirmationCode] = useState<string | null>(null);

  // Prepayment flow state
  const [bookingIntentId, setBookingIntentId] = useState<string | null>(null);
  const [, setPayToken] = useState<string | null>(null);

  const [bookingError, setBookingError] = useState<string | null>(null);

  // ── Check if patient returned from Clinio Pay ─────────────────────────────
  // Clinio Pay redirects to ?pay_token=<token>&status=succeeded|cancelled
  useEffect(() => {
    const returnPayToken = searchParams.get('pay_token');
    const returnStatus   = searchParams.get('status');

    if (returnPayToken && returnStatus === 'succeeded') {
      setPayToken(returnPayToken);
      setStep('awaiting_payment');
    } else if (returnPayToken && returnStatus === 'cancelled') {
      // Patient cancelled — return to patient step so they can re-submit.
      setStep('patient');
      setBookingError(t('booking.payment_cancelled'));
    }
  }, [searchParams, t]);

  const {
    data: bookingData,
    isLoading: isLoadingServices,
    error: servicesError,
  } = useBookableServices(clinicSlug);

  const effectiveClinicianId = anyClinicianSelected ? null : (selectedClinician?.id ?? null);

  const { data: slots, isLoading: isLoadingSlots } = useAvailableSlots(
    clinicSlug,
    selectedService?.id ?? null,
    effectiveClinicianId,
    selectedDate,
  );

  // Standard booking (no prepayment)
  const bookMutation = useBookAppointment();

  // Prepayment flow mutations
  const createIntentMutation   = useCreateBookingIntent();
  const requestPayLinkMutation = useRequestBookingPayLink();

  // Poll booking_intent status after returning from Clinio Pay
  const { data: intentStatus } = useBookingIntentStatus(
    step === 'awaiting_payment' ? bookingIntentId : null,
    { enabled: step === 'awaiting_payment' },
  );

  // When booking is confirmed via webhook → advance to confirmation
  useEffect(() => {
    if (intentStatus?.status === 'confirmed' && step === 'awaiting_payment') {
      setStep('confirmation');
    }
    if (intentStatus?.status === 'expired' && step === 'awaiting_payment') {
      setStep('patient');
      setBookingError(t('booking.payment_expired'));
    }
  }, [intentStatus, step, t]);

  const form = useForm<PatientFormValues>({
    resolver: zodResolver(PatientSchema),
    defaultValues: { first_name: '', last_name: '', email: '', phone: '', notes: '' },
  });

  // ─── Navigation helpers ────────────────────────────────────────────────────

  const goToStep = useCallback((s: Step) => setStep(s), []);
  const primaryColor = bookingData?.primary_color ?? '#6366F1';

  // ─── Date utilities ────────────────────────────────────────────────────────

  const today       = startOfDay(new Date());
  const advanceDays = bookingData?.settings.advance_days ?? 60;
  const maxDate     = addDays(today, advanceDays);

  const bookingHours = bookingData?.settings.online_booking_hours ?? {};
  const DK_WEEKDAY: Record<number, string> = {
    0: 'sondag', 1: 'mandag', 2: 'tirsdag', 3: 'onsdag',
    4: 'torsdag', 5: 'fredag', 6: 'lordag',
  };

  function isDateAvailable(d: Date): boolean {
    const day   = DK_WEEKDAY[d.getDay()];
    const hours = bookingHours[day];
    return Boolean(hours?.enabled) && !isBefore(d, today) && !isBefore(maxDate, d);
  }

  const availableDates: Date[] = [];
  for (let i = 0; i <= advanceDays && availableDates.length < 90; i++) {
    const d = addDays(today, i);
    if (isDateAvailable(d)) availableDates.push(d);
  }

  // ─── Submission ────────────────────────────────────────────────────────────

  const requiresPrepayment = Boolean(
    selectedService?.require_prepayment && (selectedService?.deposit_amount_cents ?? 0) > 0,
  );

  const onSubmit = async (values: PatientFormValues) => {
    if (!clinicSlug || !selectedService || !selectedSlot) return;
    setBookingError(null);

    const clinicianForBooking = anyClinicianSelected
      ? selectedSlot.clinician_id
      : selectedClinician!.id;

    if (requiresPrepayment) {
      // ── Prepayment flow ──────────────────────────────────────────────────
      try {
        // 1. Create booking_intent (holds the slot for 30 min).
        const intent = await createIntentMutation.mutateAsync({
          clinic_slug:  clinicSlug,
          service_id:   selectedService.id,
          clinician_id: clinicianForBooking,
          start_time:   selectedSlot.start,
          patient: {
            first_name: values.first_name,
            last_name:  values.last_name,
            email:      values.email,
            phone:      values.phone ?? undefined,
            notes:      values.notes ?? undefined,
          },
        });

        setBookingIntentId(intent.booking_intent_id);

        // 2. Call request_booking_pay_link RPC → invokes pay-create-booking-checkout
        //    Edge Function via pg_net → returns { checkout_url, pay_token }.
        const payLink = await requestPayLinkMutation.mutateAsync(intent.booking_intent_id);
        setPayToken(payLink.pay_token);

        // 3. Show payment step (redirect to Clinio Pay).
        goToStep('payment');

        // 4. Redirect to Clinio Pay hosted checkout after a brief UI flash.
        setTimeout(() => {
          window.location.href = payLink.checkout_url;
        }, 800);

      } catch (err: unknown) {
        const code = (err as { code?: string }).code;
        if (code === 'conflict') {
          setBookingError(t('booking.error_slot_taken'));
        } else if (code === 'rate-limited') {
          setBookingError(t('booking.error_rate_limited'));
        } else if (code === 'gone') {
          setBookingError(t('booking.payment_expired'));
        } else if (code === 'invalid-input') {
          setBookingError(t('booking.error_invalid_input'));
        } else if (code === 'not-found') {
          setBookingError(t('booking.error_not_found'));
        } else if (code === 'unauthorized' || code === 'forbidden') {
          setBookingError(t('booking.error_unauthorized'));
        } else if (code === 'internal-error') {
          setBookingError(t('booking.error_server'));
        } else if (err instanceof TypeError || (err as Error).message?.includes('fetch')) {
          setBookingError(t('booking.error_network'));
        } else {
          setBookingError(t('booking.error_generic'));
        }
      }
    } else {
      // ── Standard (no prepayment) flow ────────────────────────────────────
      try {
        const result = await bookMutation.mutateAsync({
          clinic_slug:  clinicSlug,
          service_id:   selectedService.id,
          clinician_id: clinicianForBooking,
          start_time:   selectedSlot.start,
          patient: {
            first_name: values.first_name,
            last_name:  values.last_name,
            email:      values.email,
            phone:      values.phone ?? undefined,
            notes:      values.notes ?? undefined,
          },
        });
        setConfirmationCode(result.confirmation_code);
        setStep('confirmation');
      } catch (err: unknown) {
        const code = (err as { code?: string }).code;
        if (code === 'conflict') {
          setBookingError(t('booking.error_slot_taken'));
        } else if (code === 'rate-limited') {
          setBookingError(t('booking.error_rate_limited'));
        } else if (code === 'invalid-input') {
          setBookingError(t('booking.error_invalid_input'));
        } else if (code === 'not-found') {
          setBookingError(t('booking.error_not_found'));
        } else if (code === 'unauthorized' || code === 'forbidden') {
          setBookingError(t('booking.error_unauthorized'));
        } else if (code === 'internal-error') {
          setBookingError(t('booking.error_server'));
        } else if (err instanceof TypeError || (err as Error).message?.includes('fetch')) {
          setBookingError(t('booking.error_network'));
        } else {
          setBookingError(t('booking.error_generic'));
        }
      }
    }
  };

  // ─── Loading / error states ────────────────────────────────────────────────

  if (isLoadingServices) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (servicesError || !bookingData) {
    const isForbidden =
      servicesError instanceof Error &&
      (servicesError.message.includes('forbidden') ||
        servicesError.message.includes('not enabled'));

    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
        <div className="w-full max-w-md rounded-2xl bg-white p-8 text-center shadow-lg">
          <h1 className="text-xl font-semibold text-gray-900">
            {isForbidden ? t('booking.disabled_title') : t('booking.not_found_title')}
          </h1>
          <p className="mt-2 text-sm text-gray-500">
            {isForbidden ? t('booking.disabled_subtitle') : t('booking.not_found_subtitle')}
          </p>
        </div>
      </div>
    );
  }

  const isProgressStep = !['payment', 'awaiting_payment', 'confirmation'].includes(step);
  const progressSteps  = requiresPrepayment ? PREPAY_STEPS.slice(0, 5) : STANDARD_STEPS.slice(0, 5);
  const currentStepIndex = progressSteps.indexOf(step as never);

  const isSubmitting =
    bookMutation.isPending ||
    createIntentMutation.isPending ||
    requestPayLinkMutation.isPending;

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="mx-auto flex max-w-2xl items-center gap-4 px-4 py-5">
          {bookingData.clinic_logo && (
            <img
              src={bookingData.clinic_logo}
              alt={bookingData.clinic_name}
              className="h-10 w-auto object-contain"
            />
          )}
          <div>
            <h1 className="text-lg font-semibold text-gray-900">{bookingData.clinic_name}</h1>
            <p className="text-sm text-gray-500">{t('booking.header_subtitle')}</p>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-2xl px-4 py-8">
        {isProgressStep && (
          <ProgressBar
            steps={progressSteps}
            currentIndex={currentStepIndex}
            primaryColor={primaryColor}
            t={t}
          />
        )}

        <div className="mt-6 rounded-2xl bg-white p-6 shadow-sm">
          {/* Step 1: Select service */}
          {step === 'service' && (
            <ServiceStep
              services={bookingData.services}
              primaryColor={primaryColor}
              t={t}
              onSelect={(svc) => {
                setSelectedService(svc);
                setSelectedClinician(null);
                setAnyClinicianSelected(false);
                setSelectedDate(null);
                setSelectedSlot(null);
                goToStep('clinician');
              }}
            />
          )}

          {/* Step 2: Select clinician */}
          {step === 'clinician' && selectedService && (
            <ClinicianStep
              clinicians={bookingData.clinicians}
              primaryColor={primaryColor}
              t={t}
              onSelect={(c) => {
                setSelectedClinician(c);
                setAnyClinicianSelected(false);
                setSelectedDate(null);
                setSelectedSlot(null);
                goToStep('date');
              }}
              onAny={() => {
                setSelectedClinician(null);
                setAnyClinicianSelected(true);
                setSelectedDate(null);
                setSelectedSlot(null);
                goToStep('date');
              }}
              onBack={() => goToStep('service')}
            />
          )}

          {/* Step 3: Select date */}
          {step === 'date' && (
            <DateStep
              availableDates={availableDates}
              primaryColor={primaryColor}
              t={t}
              onSelect={(d) => {
                setSelectedDate(d);
                setSelectedSlot(null);
                goToStep('slot');
              }}
              onBack={() => goToStep('clinician')}
            />
          )}

          {/* Step 4: Select time slot */}
          {step === 'slot' && selectedDate && (
            <SlotStep
              slots={slots ?? []}
              isLoading={isLoadingSlots}
              clinicians={bookingData.clinicians}
              selectedDate={selectedDate}
              primaryColor={primaryColor}
              t={t}
              onSelect={(slot) => {
                setSelectedSlot(slot);
                goToStep('patient');
              }}
              onBack={() => goToStep('date')}
            />
          )}

          {/* Step 5: Patient info */}
          {step === 'patient' && (
            <PatientStep
              form={form}
              selectedService={selectedService!}
              selectedSlot={selectedSlot!}
              primaryColor={primaryColor}
              bookingError={bookingError}
              isSubmitting={isSubmitting}
              requiresPrepayment={requiresPrepayment}
              t={t}
              onSubmit={onSubmit}
              onBack={() => goToStep('slot')}
            />
          )}

          {/* Step 6a: Payment redirect (prepayment only) */}
          {step === 'payment' && (
            <PaymentRedirectStep
              service={selectedService!}
              primaryColor={primaryColor}
              t={t}
            />
          )}

          {/* Step 6b: Awaiting payment confirmation (prepayment only) */}
          {step === 'awaiting_payment' && (
            <AwaitingPaymentStep
              intentStatus={intentStatus?.status ?? 'payment_submitted'}
              primaryColor={primaryColor}
              t={t}
              onCancel={() => {
                setStep('patient');
                setBookingError(t('booking.payment_cancelled'));
              }}
            />
          )}

          {/* Step 7: Confirmation */}
          {step === 'confirmation' && selectedService && selectedSlot && (
            <ConfirmationStep
              clinicName={bookingData.clinic_name}
              serviceName={selectedService.name}
              selectedSlot={selectedSlot}
              confirmationCode={confirmationCode}
              wasPrepaid={requiresPrepayment}
              primaryColor={primaryColor}
              t={t}
            />
          )}
        </div>
      </main>
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function ProgressBar({
  steps,
  currentIndex,
  primaryColor,
  t,
}: {
  steps: Step[];
  currentIndex: number;
  primaryColor: string;
  t: (k: string, options?: Record<string, unknown>) => string;
}) {
  const labels: Record<Step, string> = {
    service:         t('booking.step_service'),
    clinician:       t('booking.step_clinician'),
    date:            t('booking.step_date'),
    slot:            t('booking.step_slot'),
    patient:         t('booking.step_patient'),
    payment:         t('booking.step_payment'),
    awaiting_payment: t('booking.step_awaiting_payment'),
    confirmation:    t('booking.step_confirmation'),
  };

  return (
    <div className="flex items-center gap-1">
      {steps.map((s, i) => (
        <div key={s} className="flex flex-1 items-center gap-1">
          <div className="flex flex-col items-center gap-1">
            <div
              className="flex h-7 w-7 items-center justify-center rounded-full text-xs font-medium transition-colors"
              style={{
                backgroundColor: i <= currentIndex ? primaryColor : '#e5e7eb',
                color: i <= currentIndex ? '#fff' : '#6b7280',
              }}
            >
              {i < currentIndex ? <Check className="h-4 w-4" /> : <span>{i + 1}</span>}
            </div>
            <span className="hidden text-[10px] text-gray-500 sm:block">{labels[s]}</span>
          </div>
          {i < steps.length - 1 && (
            <div
              className="h-0.5 flex-1 transition-colors"
              style={{ backgroundColor: i < currentIndex ? primaryColor : '#e5e7eb' }}
            />
          )}
        </div>
      ))}
    </div>
  );
}

function ServiceStep({
  services,
  primaryColor: _primaryColor,
  t,
  onSelect,
}: {
  services: BookableService[];
  primaryColor: string;
  t: (k: string, options?: Record<string, unknown>) => string;
  onSelect: (s: BookableService) => void;
}) {
  return (
    <div>
      <h2 className="mb-1 text-lg font-semibold text-gray-900">{t('booking.service_title')}</h2>
      <p className="mb-5 text-sm text-gray-500">{t('booking.service_subtitle')}</p>

      {services.length === 0 ? (
        <p className="text-sm text-gray-400">{t('booking.no_services')}</p>
      ) : (
        <div className="space-y-3">
          {services.map((svc) => (
            <button
              key={svc.id}
              onClick={() => onSelect(svc)}
              className="group w-full rounded-xl border border-gray-200 p-4 text-left transition-colors hover:border-transparent hover:shadow-md focus:outline-none"
            >
              <div className="flex items-start gap-3">
                <div
                  className="mt-0.5 h-3 w-3 flex-shrink-0 rounded-full"
                  style={{ backgroundColor: svc.color }}
                />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-gray-900">{svc.name}</span>
                    <ChevronRight className="h-4 w-4 flex-shrink-0 text-gray-400" />
                  </div>
                  {svc.description && (
                    <p className="mt-0.5 text-sm text-gray-500">{svc.description}</p>
                  )}
                  <div className="mt-2 flex items-center gap-4 text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <Clock className="h-3.5 w-3.5" />
                      {svc.duration_minutes} {t('booking.minutes')}
                    </span>
                    {svc.price_cents > 0 && (
                      <span>
                        {(svc.price_cents / 100).toLocaleString('da-DK', {
                          style: 'currency',
                          currency: 'DKK',
                          minimumFractionDigits: 0,
                        })}
                      </span>
                    )}
                    {svc.require_prepayment && svc.deposit_amount_cents > 0 && (
                      <span className="flex items-center gap-1 font-medium text-amber-600">
                        <CreditCard className="h-3.5 w-3.5" />
                        {t('booking.prepayment_required', {
                          amount: (svc.deposit_amount_cents / 100).toLocaleString('da-DK', {
                            style: 'currency',
                            currency: 'DKK',
                            minimumFractionDigits: 0,
                          }),
                        })}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function ClinicianStep({
  clinicians, primaryColor, t, onSelect, onAny, onBack,
}: {
  clinicians: BookableClinician[];
  primaryColor: string;
  t: (k: string, options?: Record<string, unknown>) => string;
  onSelect: (c: BookableClinician) => void;
  onAny: () => void;
  onBack: () => void;
}) {
  return (
    <div>
      <h2 className="mb-1 text-lg font-semibold text-gray-900">{t('booking.clinician_title')}</h2>
      <p className="mb-5 text-sm text-gray-500">{t('booking.clinician_subtitle')}</p>
      <div className="space-y-3">
        <button
          onClick={onAny}
          className="group w-full rounded-xl border border-gray-200 p-4 text-left transition-colors hover:border-transparent hover:shadow-md focus:outline-none"
        >
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100">
              <Stethoscope className="h-5 w-5 text-gray-500" />
            </div>
            <div className="flex-1">
              <span className="font-medium text-gray-900">{t('booking.any_clinician')}</span>
              <p className="text-sm text-gray-500">{t('booking.any_clinician_desc')}</p>
            </div>
            <ChevronRight className="h-4 w-4 flex-shrink-0 text-gray-400" />
          </div>
        </button>
        {clinicians.map((c) => (
          <button
            key={c.id}
            onClick={() => onSelect(c)}
            className="group w-full rounded-xl border border-gray-200 p-4 text-left transition-colors hover:border-transparent hover:shadow-md focus:outline-none"
          >
            <div className="flex items-center gap-3">
              {c.photo_url ? (
                <img
                  src={c.photo_url}
                  alt={c.name}
                  className="h-10 w-10 flex-shrink-0 rounded-full object-cover"
                />
              ) : (
                <div
                  className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full"
                  style={{ backgroundColor: primaryColor + '20' }}
                >
                  <User className="h-5 w-5" style={{ color: primaryColor }} />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-gray-900">{c.name}</span>
                  <ChevronRight className="h-4 w-4 flex-shrink-0 text-gray-400" />
                </div>
                {c.title && <p className="text-sm text-gray-500">{c.title}</p>}
                {c.bio && <p className="mt-1 line-clamp-2 text-sm text-gray-400">{c.bio}</p>}
              </div>
            </div>
          </button>
        ))}
      </div>
      <BackButton onClick={onBack} t={t} />
    </div>
  );
}

function DateStep({
  availableDates, primaryColor: _primaryColor, t, onSelect, onBack,
}: {
  availableDates: Date[];
  primaryColor: string;
  t: (k: string, options?: Record<string, unknown>) => string;
  onSelect: (date: string) => void;
  onBack: () => void;
}) {
  return (
    <div>
      <h2 className="mb-1 text-lg font-semibold text-gray-900">{t('booking.date_title')}</h2>
      <p className="mb-5 text-sm text-gray-500">{t('booking.date_subtitle')}</p>
      {availableDates.length === 0 ? (
        <p className="text-sm text-gray-400">{t('booking.no_dates')}</p>
      ) : (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {availableDates.map((d) => {
            const dateStr = format(d, 'yyyy-MM-dd');
            return (
              <button
                key={dateStr}
                onClick={() => onSelect(dateStr)}
                className="rounded-xl border border-gray-200 px-3 py-3 text-left transition-colors hover:border-transparent hover:shadow-md focus:outline-none"
              >
                <div className="text-sm font-medium capitalize text-gray-900">
                  {format(d, 'EEEE', { locale: da })}
                </div>
                <div className="text-sm text-gray-500">{format(d, 'd. MMM', { locale: da })}</div>
              </button>
            );
          })}
        </div>
      )}
      <BackButton onClick={onBack} t={t} />
    </div>
  );
}

function SlotStep({
  slots, isLoading, clinicians, selectedDate, primaryColor: _primaryColor, t, onSelect, onBack,
}: {
  slots: AvailableSlot[];
  isLoading: boolean;
  clinicians: BookableClinician[];
  selectedDate: string;
  primaryColor: string;
  t: (k: string, options?: Record<string, unknown>) => string;
  onSelect: (slot: AvailableSlot) => void;
  onBack: () => void;
}) {
  const clinicianMap = new Map(clinicians.map((c) => [c.id, c]));
  const parsedDate   = parseISO(selectedDate);

  return (
    <div>
      <h2 className="mb-1 text-lg font-semibold text-gray-900">{t('booking.slot_title')}</h2>
      <p className="mb-1 text-sm text-gray-500">
        {format(parsedDate, 'EEEE d. MMMM yyyy', { locale: da })}
      </p>
      <p className="mb-5 text-sm text-gray-400">{t('booking.slot_subtitle')}</p>
      {isLoading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      ) : slots.length === 0 ? (
        <p className="text-sm text-gray-400">{t('booking.no_slots')}</p>
      ) : (
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
          {slots.map((slot, idx) => {
            const startDate = parseISO(slot.start);
            const clinician = clinicianMap.get(slot.clinician_id);
            return (
              <button
                key={idx}
                onClick={() => onSelect(slot)}
                className="rounded-xl border border-gray-200 px-2 py-3 text-center transition-colors hover:border-transparent hover:shadow-md focus:outline-none"
              >
                <div className="text-sm font-semibold text-gray-900">
                  {format(startDate, 'HH:mm')}
                </div>
                {clinician && (
                  <div className="mt-0.5 truncate text-[10px] text-gray-400">
                    {clinician.name.split(' ')[0]}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}
      <BackButton onClick={onBack} t={t} />
    </div>
  );
}

function PatientStep({
  form,
  selectedService,
  selectedSlot,
  primaryColor,
  bookingError,
  isSubmitting,
  requiresPrepayment,
  t,
  onSubmit,
  onBack,
}: {
  form: ReturnType<typeof useForm<PatientFormValues>>;
  selectedService: BookableService;
  selectedSlot: AvailableSlot;
  primaryColor: string;
  bookingError: string | null;
  isSubmitting: boolean;
  requiresPrepayment: boolean;
  t: (k: string, options?: Record<string, unknown>) => string;
  onSubmit: (v: PatientFormValues) => Promise<void>;
  onBack: () => void;
}) {
  const slotStart = parseISO(selectedSlot.start);

  return (
    <div>
      <h2 className="mb-1 text-lg font-semibold text-gray-900">{t('booking.patient_title')}</h2>
      <p className="mb-5 text-sm text-gray-500">{t('booking.patient_subtitle')}</p>

      {/* Summary */}
      <div className="mb-5 rounded-xl bg-gray-50 p-4 text-sm">
        <div className="flex items-center gap-2 text-gray-700">
          <Calendar className="h-4 w-4 flex-shrink-0" style={{ color: primaryColor }} />
          <span className="font-medium">{selectedService.name}</span>
          <span className="text-gray-400">·</span>
          <span>{format(slotStart, 'EEEE d. MMMM kk:mm', { locale: da })}</span>
        </div>
        {requiresPrepayment && selectedService.deposit_amount_cents > 0 && (
          <div className="mt-2 flex items-center gap-2 text-amber-700">
            <CreditCard className="h-4 w-4 flex-shrink-0" />
            <span>
              {t('booking.prepayment_notice', {
                amount: (selectedService.deposit_amount_cents / 100).toLocaleString('da-DK', {
                  style: 'currency',
                  currency: 'DKK',
                  minimumFractionDigits: 0,
                }),
              })}
            </span>
          </div>
        )}
      </div>

      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <FormField label={t('booking.field_first_name')} error={form.formState.errors.first_name?.message}>
            <input
              {...form.register('first_name')}
              className="input-field"
              placeholder={t('booking.field_first_name_placeholder')}
              autoComplete="given-name"
            />
          </FormField>
          <FormField label={t('booking.field_last_name')} error={form.formState.errors.last_name?.message}>
            <input
              {...form.register('last_name')}
              className="input-field"
              placeholder={t('booking.field_last_name_placeholder')}
              autoComplete="family-name"
            />
          </FormField>
        </div>

        <FormField label={t('booking.field_email')} error={form.formState.errors.email?.message}>
          <input
            {...form.register('email')}
            type="email"
            className="input-field"
            placeholder={t('booking.field_email_placeholder')}
            autoComplete="email"
          />
        </FormField>

        <FormField label={t('booking.field_phone')} error={undefined}>
          <input
            {...form.register('phone')}
            type="tel"
            className="input-field"
            placeholder={t('booking.field_phone_placeholder')}
            autoComplete="tel"
          />
        </FormField>

        <FormField label={t('booking.field_notes')} error={undefined}>
          <textarea
            {...form.register('notes')}
            rows={3}
            className="input-field resize-none"
            placeholder={t('booking.field_notes_placeholder')}
          />
        </FormField>

        {bookingError && (
          <div className="flex items-start gap-2 rounded-xl bg-red-50 p-3 text-sm text-red-700">
            <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span>{bookingError}</span>
          </div>
        )}

        <div className="flex items-center gap-3 pt-2">
          <button
            type="button"
            onClick={onBack}
            className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
          >
            <ChevronLeft className="h-4 w-4" />
            {t('booking.back')}
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="flex flex-1 items-center justify-center gap-2 rounded-xl py-3 text-sm font-medium text-white transition-opacity disabled:opacity-60"
            style={{ backgroundColor: primaryColor }}
          >
            {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            {isSubmitting
              ? t('booking.submitting')
              : requiresPrepayment
                ? t('booking.submit_prepay')
                : t('booking.submit')}
          </button>
        </div>
      </form>
    </div>
  );
}

function PaymentRedirectStep({
  service,
  primaryColor,
  t,
}: {
  service: BookableService;
  primaryColor: string;
  t: (k: string, options?: Record<string, unknown>) => string;
}) {
  return (
    <div className="py-4 text-center">
      <div
        className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full"
        style={{ backgroundColor: primaryColor + '20' }}
      >
        <CreditCard className="h-7 w-7" style={{ color: primaryColor }} />
      </div>
      <h2 className="text-lg font-semibold text-gray-900">{t('booking.payment_redirect_title')}</h2>
      <p className="mt-1 text-sm text-gray-500">
        {t('booking.payment_redirect_subtitle', {
          amount: (service.deposit_amount_cents / 100).toLocaleString('da-DK', {
            style: 'currency',
            currency: 'DKK',
            minimumFractionDigits: 0,
          }),
        })}
      </p>
      <div className="mt-6 flex justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
      <p className="mt-3 text-xs text-gray-400">{t('booking.payment_redirect_wait')}</p>
    </div>
  );
}

function AwaitingPaymentStep({
  intentStatus,
  primaryColor,
  t,
  onCancel,
}: {
  intentStatus: string;
  primaryColor: string;
  t: (k: string, options?: Record<string, unknown>) => string;
  onCancel: () => void;
}) {
  return (
    <div className="py-4 text-center">
      <div
        className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full"
        style={{ backgroundColor: primaryColor + '20' }}
      >
        <Loader2 className="h-7 w-7 animate-spin" style={{ color: primaryColor }} />
      </div>
      <h2 className="text-lg font-semibold text-gray-900">{t('booking.awaiting_payment_title')}</h2>
      <p className="mt-1 text-sm text-gray-500">{t('booking.awaiting_payment_subtitle')}</p>
      {intentStatus === 'payment_submitted' && (
        <p className="mt-4 text-xs text-gray-400">{t('booking.awaiting_payment_note')}</p>
      )}
      <button
        type="button"
        onClick={onCancel}
        className="mt-6 text-sm text-gray-400 underline hover:text-gray-600"
      >
        {t('booking.awaiting_payment_cancel')}
      </button>
    </div>
  );
}

function ConfirmationStep({
  clinicName,
  serviceName,
  selectedSlot,
  confirmationCode,
  wasPrepaid,
  primaryColor,
  t,
}: {
  clinicName: string;
  serviceName: string;
  selectedSlot: AvailableSlot;
  confirmationCode: string | null;
  wasPrepaid: boolean;
  primaryColor: string;
  t: (k: string, options?: Record<string, unknown>) => string;
}) {
  const slotStart = parseISO(selectedSlot.start);

  return (
    <div className="text-center">
      <div
        className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full"
        style={{ backgroundColor: primaryColor + '20' }}
      >
        <Check className="h-7 w-7" style={{ color: primaryColor }} />
      </div>
      <h2 className="text-xl font-semibold text-gray-900">{t('booking.confirmed_title')}</h2>
      <p className="mt-1 text-sm text-gray-500">
        {wasPrepaid
          ? t('booking.confirmed_prepaid_subtitle')
          : t('booking.confirmed_subtitle')}
      </p>

      <div className="mt-6 space-y-3 rounded-xl bg-gray-50 p-5 text-left">
        <Row label={t('booking.confirmed_service')} value={serviceName} />
        <Row
          label={t('booking.confirmed_time')}
          value={format(slotStart, 'EEEE d. MMMM yyyy kk:mm', { locale: da })}
        />
        <Row label={t('booking.confirmed_clinic')} value={clinicName} />
      </div>

      {confirmationCode && (
        <div className="mt-5 rounded-xl border-2 p-4" style={{ borderColor: primaryColor }}>
          <p className="text-xs uppercase tracking-wide text-gray-500">
            {t('booking.confirmation_code')}
          </p>
          <p
            className="mt-1 font-mono text-2xl font-bold tracking-widest"
            style={{ color: primaryColor }}
          >
            {confirmationCode}
          </p>
          <p className="mt-1 text-xs text-gray-400">{t('booking.confirmation_code_note')}</p>
        </div>
      )}

      <p className="mt-5 text-sm text-gray-500">{t('booking.email_sent_note')}</p>
    </div>
  );
}

// ─── Tiny helpers ─────────────────────────────────────────────────────────────

function FormField({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      <label className="block text-sm font-medium text-gray-700">{label}</label>
      {children}
      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );
}

function BackButton({ onClick, t }: { onClick: () => void; t: (k: string, options?: Record<string, unknown>) => string }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="mt-5 flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
    >
      <ChevronLeft className="h-4 w-4" />
      {t('booking.back')}
    </button>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <span className="flex-shrink-0 text-sm text-gray-500">{label}</span>
      <span className="text-right text-sm font-medium text-gray-900">{value}</span>
    </div>
  );
}
